import { Button, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { find } from 'ramda';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Project } from '../../Projects/projects';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { RootState } from '../../../redux/Store';
import { ApplicationActions, findRapidRobotByShareCode, RAPID_ROBOT_COMPLEXITY, ROBOT_TEMPLATES } from '../../../utils/constants';
import checkProjectName from '../../../utils/validation/checkProjectName';
import { duplicateProject } from '../../../utils/projectDuplication';
import { useDriveUnitPortsForProject } from '../../Dashboard/persistDashboardWidgetContent';
import { checkForUnsupportedCharacters, createProjectFromPlatformCode } from '../../../utils/helpers.ts';
import { readSwerveSlotPythonSource } from '../../../utils/readSwerveSlotPythonSource';
import { getSaveSlotDisplayProgramName } from '../../../utils/saveSlotProjectId';
import { createSwerveSlotBackupProject } from '../../../utils/swerveSlotBackupProject';
import ProgramManagementErrorDialog from '../ErrorDialogs/ProgramManagementErrorDialog';

interface ProjectNameDialogProps {
  onClose: () => void;
  open: boolean;
}

const ProjectNameDialog = ({ onClose, open }: ProjectNameDialogProps) => {
  const {
    title,
    isEditing,
    projectId,
    projectTemplate,
    rapidRobotCode,
    saveFromSwerveSlot,
    swerveSlotAssetId,
    swerveSlotProgramTitle,
    swerveSlotBpsProjectId,
    swerveSlotBpsProjectLastModifiedUtcMs,
    copyFromProjectId,
  } = useSelector((state: RootState) => state.application.openDialog.options);
  const [projectName, setProjectName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [programManagementErrorOpen, setProgramManagementErrorOpen] = useState(false);
  const initializedDialogKey = useRef<string | null>(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { projects, loadedProject } = useSelector((state: RootState) => state.project);
  const driveUnitPortsForCopy = useDriveUnitPortsForProject(copyFromProjectId);

  const findProject = (id: string, projectList: Array<Project>) => {
    const project = find((p: Project) => p.id === id, projectList);
    return project;
  };

  const resolveExistingProject = (id: string) => findProject(id, projects) ?? (loadedProject?.id === id ? loadedProject : undefined);

  /** Prefer the project currently open in the editor over a same-id persisted list entry (share-link viewer flow). */
  const resolveCopySourceProject = (id: string) => (loadedProject?.id === id ? loadedProject : findProject(id, projects));

  const existingProject = isEditing && projectId ? resolveExistingProject(projectId) : undefined;
  const copySourceProject = copyFromProjectId ? resolveCopySourceProject(copyFromProjectId) : undefined;
  const isCopySourceUnavailable = Boolean(copyFromProjectId && !copySourceProject);
  const dialogKey = open
    ? `${isEditing ? 'edit' : copyFromProjectId ? 'copy' : saveFromSwerveSlot ? 'slot' : 'create'}:${projectId ?? ''}:${copyFromProjectId ?? ''}:${
        swerveSlotAssetId ?? ''
      }:${swerveSlotProgramTitle ?? ''}:${rapidRobotCode ?? ''}`
    : null;

  useEffect(() => {
    if (!open) {
      initializedDialogKey.current = null;
      setIsSubmitting(false);
      setProgramManagementErrorOpen(false);
      return;
    }

    if (initializedDialogKey.current === dialogKey) {
      return;
    }

    if (isEditing) {
      if (!existingProject) return;
      setProjectName(existingProject.name || '');
    } else if (saveFromSwerveSlot) {
      const defaultName = getSaveSlotDisplayProgramName(swerveSlotProgramTitle).trim() || swerveSlotAssetId || '';
      setProjectName(defaultName);
    } else if (copyFromProjectId) {
      if (!copySourceProject) return;
      setProjectName((current) => {
        if (current.trim() !== '') return current;
        return t('projects.copyOf', 'Copy of "{{name}}"', { name: copySourceProject.name });
      });
    } else if (rapidRobotCode) {
      const rapidRobot = findRapidRobotByShareCode(rapidRobotCode);
      setProjectName((current) => {
        if (current.trim() !== '') return current;
        if (!rapidRobot) return '';
        const baseName = t(rapidRobot.nameKey, rapidRobot.name);
        const displayName =
          rapidRobot.complexity === RAPID_ROBOT_COMPLEXITY.MotorsAndSensors
            ? t('rapidRobots.projectName.withSensors', '{{name}} + SENSORS', { name: baseName })
            : baseName;
        return t('projects.copyOf', 'Copy of "{{name}}"', {
          name: displayName,
        });
      });
    } else {
      setProjectName('');
    }

    initializedDialogKey.current = dialogKey;
  }, [
    copyFromProjectId,
    copySourceProject,
    dialogKey,
    existingProject,
    isEditing,
    open,
    rapidRobotCode,
    saveFromSwerveSlot,
    swerveSlotAssetId,
    swerveSlotProgramTitle,
    t,
  ]);

  const handleInput = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setProjectName(event.target.value);
  };

  const upsertProject = (newProject: Project) => {
    let _projects: Array<Project>;
    if (findProject(newProject.id, projects)) {
      _projects = projects.map((p) => (p.id === newProject.id ? newProject : p));
    } else {
      _projects = [newProject, ...projects];
    }

    dispatch(projectSlice.actions.onUpdateProjects(_projects));
  };

  const createProjectFromSwerveSlot = async (): Promise<Project> => {
    if (!swerveSlotAssetId) {
      throw new Error('No slot asset id provided for save as new');
    }

    const pythonSource = await readSwerveSlotPythonSource(swerveSlotAssetId);
    const newProjectId = crypto.randomUUID();
    const resolvedProjectName = checkProjectName(projectName, newProjectId);
    const newFilename = checkForUnsupportedCharacters(resolvedProjectName);
    const project = await createSwerveSlotBackupProject({
      pythonSource,
      slot: {
        programTitle: swerveSlotProgramTitle,
        bpsProjectId: swerveSlotBpsProjectId,
        bpsProjectLastModifiedUtcMs: swerveSlotBpsProjectLastModifiedUtcMs,
      },
      fallbackName: swerveSlotAssetId,
      projectName: resolvedProjectName,
    });

    return {
      ...project,
      id: newProjectId,
      name: resolvedProjectName,
      file: `${newFilename}.py`,
    };
  };

  const handleCreateProject = async () => {
    if (isSubmitting) return;

    if (isEditing) {
      if (!existingProject) return;
      const newProject = { ...existingProject, name: projectName };
      const newFilename = checkForUnsupportedCharacters(newProject.name);
      newProject.file = `${newFilename}.py`;
      newProject.name = checkProjectName(projectName, newProject.id);
      upsertProject(newProject);
      onClose();
    } else if (saveFromSwerveSlot) {
      let newProject: Project;
      setIsSubmitting(true);

      try {
        newProject = await createProjectFromSwerveSlot();
      } catch (err) {
        console.error('Failed to save project from robot slot:', err);
        setProgramManagementErrorOpen(true);
        return;
      } finally {
        setIsSubmitting(false);
      }

      dispatch(projectSlice.actions.onAddProject(newProject));
      dispatch(
        applicationSlice.actions.onOpenDialog({
          ...ApplicationActions.Dialogs.PROJECT_SAVED,
          options: {
            ...ApplicationActions.Dialogs.PROJECT_SAVED.options,
            projectName: newProject.name,
          },
        })
      );
    } else if (copyFromProjectId) {
      if (!copySourceProject) return;
      setIsSubmitting(true);
      try {
        const duplicate = duplicateProject(copySourceProject, projectName, driveUnitPortsForCopy);
        dispatch(projectSlice.actions.onAddProject(duplicate));
        dispatch(projectSlice.actions.onCreateProject(duplicate));
        window.projectId = duplicate.id;
        onClose();
        navigate(`/editor/${duplicate.id}`);
      } catch (err) {
        console.error('Failed to copy project:', err);
        setIsSubmitting(false);
      }
    } else {
      let newProject: Project;
      setIsSubmitting(true);

      try {
        const codeFromTemplate = projectTemplate ? ROBOT_TEMPLATES.find((t) => t.key === projectTemplate)?.shareCode ?? null : null;

        const shareCode = rapidRobotCode ?? codeFromTemplate;
        if (!shareCode) throw new Error('No share code provided for project creation');

        newProject = await createProjectFromPlatformCode(projectName, shareCode);
      } catch (err) {
        console.error('Failed to create project:', err);
        return;
      } finally {
        setIsSubmitting(false);
      }

      const newFilename = checkForUnsupportedCharacters(newProject.name);
      newProject.file = `${newFilename}.py`;
      newProject.name = checkProjectName(projectName, newProject.id);
      upsertProject(newProject);
      dispatch(projectSlice.actions.onCreateProject(newProject));
      window.projectId = newProject.id;
      onClose();
      navigate(`/editor/${newProject.id}`);
    }
  };

  const handleCancelProject = () => {
    onClose();
  };

  return (
    <>
      <DialogTitle textAlign="center">
        {/* Name Your Project || Edit Project Name */}
        <Trans i18nKey={title} />
      </DialogTitle>
      <DialogContent sx={{ width: 500, overflow: 'hidden' }}>
        <Stack
          component="form"
          direction="column"
          spacing={2}
          marginTop={1}
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          onSubmit={(e) => {
            e.preventDefault();
            void handleCreateProject();
          }}
        >
          <TextField
            id="project-name"
            label={t('projectNameDialog.input.label', 'Project Name')}
            placeholder={t('projectNameDialog.input.placeholder', 'Untitled Project')}
            value={projectName}
            onChange={handleInput}
            disabled={isSubmitting}
            error={isCopySourceUnavailable}
            helperText={
              isCopySourceUnavailable
                ? t('projectNameDialog.error.copySourceUnavailable', 'Unable to load the shared program to copy. Close this dialog and try again.')
                : undefined
            }
            sx={{ width: 200 }}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Stack direction="row" display="flex" justifyContent="space-between" width={'100%'}>
            <Button type="button" onClick={handleCancelProject} disabled={isSubmitting}>
              <Trans i18nKey="projectNameDialog.button.cancel">Cancel</Trans>
            </Button>

            <Button type="submit" disabled={isSubmitting || isCopySourceUnavailable}>
              {isEditing || saveFromSwerveSlot ? (
                <Trans i18nKey="projectNameDialog.button.Save">Save</Trans>
              ) : (
                <Trans i18nKey="projectNameDialog.button.create">Create</Trans>
              )}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <ProgramManagementErrorDialog open={programManagementErrorOpen} onClose={() => setProgramManagementErrorOpen(false)} />
    </>
  );
};

export default ProjectNameDialog;
